import * as React from "react";

import { cn } from "@/libs/cn";

import Spinner from "../Spinner";
import { TabsV2Content, TabsV2List, TabsV2Root, TabsV2Trigger } from "../../ui/TabsV2Primitive";

interface TabData {
  key: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

interface TabsV2Props {
  tabData: TabData[];
  defaultValue?: string;
  value?: string;
  isLoading?: boolean;
  iconPosition?: "left" | "right";
  onTabChange?: (key: string) => void;
  className?: string;
  tabsListClassName?: string;
  tabsTriggerClassName?: string;
  tabsContentClassName?: string;
}

const TabsV2 = ({
  tabData,
  defaultValue,
  value,
  isLoading,
  iconPosition,
  onTabChange,
  className,
  tabsListClassName,
  tabsTriggerClassName,
  tabsContentClassName,
}: TabsV2Props) => {
  const initialValue = value ?? defaultValue ?? tabData[0]?.key;
  const [activeTab, setActiveTab] = React.useState(initialValue);
  const [indicator, setIndicator] = React.useState({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = React.useState(false);

  const triggerRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const updateIndicator = React.useCallback(
    (tab: string) => {
      const idx = tabData.findIndex((t) => t.key === tab);
      const el = triggerRefs.current[idx];
      if (el) {
        setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        setIndicatorReady(true);
      }
    },
    [tabData],
  );

  // Sync with controlled value
  React.useEffect(() => {
    if (value !== undefined && value !== activeTab) {
      setActiveTab(value);
    }
  }, [value]);

  // Update indicator whenever activeTab changes
  React.useEffect(() => {
    updateIndicator(activeTab);
  }, [activeTab, updateIndicator]);

  // Re-measure on resize
  React.useEffect(() => {
    const handleResize = () => updateIndicator(activeTab);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab, updateIndicator]);

  const handleValueChange = (tab: string) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  return (
    <TabsV2Root className={cn("flex w-full flex-col", className)} value={activeTab} onValueChange={handleValueChange}>
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <TabsV2List className={cn("relative flex w-full border-b border-slate-200", tabsListClassName)}>
            {tabData.map((tab, idx) => (
              <TabsV2Trigger
                ref={(el) => {
                  triggerRefs.current[idx] = el;
                }}
                className={cn(iconPosition === "right" ? "flex-row-reverse" : "flex-row", "flex items-center gap-1", tabsTriggerClassName)}
                key={`trigger-v2-${tab.key}`}
                value={tab.key}
                disabled={tab.disabled}
              >
                {tab.icon}
                {tab.label}
              </TabsV2Trigger>
            ))}

            {/* Sliding indicator */}
            <div
              aria-hidden
              className={cn(
                "absolute bottom-[-1px] h-[2px] bg-ui-text-dark",
                "transition-[left,width] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
                indicatorReady ? "opacity-100" : "opacity-0",
              )}
              style={{ left: indicator.left, width: indicator.width }}
            />
          </TabsV2List>

          {tabData.map((tab) => (
            <TabsV2Content className={tabsContentClassName} key={`content-v2-${tab.key}`} value={tab.key}>
              {tab.content}
            </TabsV2Content>
          ))}
        </>
      )}
    </TabsV2Root>
  );
};

export default TabsV2;
